import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ISubHeaderProps } from "./interface";
import { Segmented, Button, Tooltip } from "src/assets/imports/antdComponents";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import {
	FeatureKeysType,
	ReportsHomePageTabType,
	ReportsPageViewType,
	StudyPageTabType,
	StudyPageTabTypeRevamp,
	StudyPageViewType,
	userAccessType,
} from "src/pages/Studies/models/interfaces";
import { ModuleType } from "src/components/SubHeader/interface";
import { useLocation, useNavigate } from "react-router-dom";
import { ILevel1, ITag } from "../Searchbar/interface";
import { setSearchText, setSelectedCollaborators, setSelectedStudyPageTab, setSelectedStudyPageView } from "src/pages/Studies/states/studiesSlice";
import { useFeatures } from "src/config/featureManager";
import {
	resetSelectedReportState,
	setReportsSearchText,
	setSelectedReportsPageTab,
	setSelectedReportsPageView,
	setReportsSelectedCollaborators,
} from "src/pages/Reports/states/reportsSlice";
import Searchbar from "../Searchbar";
import Folder from "public/icon/Folder.svg";
import "./style.css";
import MultiSelectStudyDropdownWrapper from "src/pages/NewAskKural/components/DynamicIslandInput/components/MultiSelectStudyDropdownWrapper";

const HomePageSubHeader: React.FC<ISubHeaderProps> = ({
	allData = [],
	renderedFrom,
	customTitle,
	customButtons,
	secondButton,
	topRowButtons,
	subtitle,
	type = "default",
	tags = [],
	onTagSelect,
	currentFolder,
	onBackClick,
	onFolderSearchClick,
	selectedTypes,
	setSelectedTypes,
	setLoading,
}) => {
	const { t } = useTranslation();
	const { isFeatureAvailable } = useFeatures();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Feature flags
	const isReportsModuleEnabled = isFeatureAvailable(FeatureKeysType.Storyboard);

	const [activeTag, setActiveTag] = useState<string>("All");
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [tagList, setTagList] = useState<string[]>();
	const [fromModal, setFromModal] = useState<boolean>(false);
	const [isAddCollabModalOpen, setIsAddCollabModalOpen] = useState(false);
	const [collabModalKey, setCollabModalKey] = useState(0);

	const isStudies = useMemo(() => {
		return renderedFrom === ModuleType.Studies;
	}, [renderedFrom]);

	const isReports = useMemo(() => {
		return renderedFrom === ModuleType.Reports;
	}, [renderedFrom]);

	const pageTitle = customTitle || (!isStudies ? t("Reports") : t("Studies"));
	const visibleTagCount = isStudies ? 6 : 2;
	const remainingTags = tagList?.length - visibleTagCount;
	const studiesState = useSelector((state: RootState) => state.studiesSlice);
	const reportsState = useSelector((state: RootState) => state.reportsSlice);
	const isFolderEnabled = isFeatureAvailable(FeatureKeysType.EnableFolder);

	const moduleState = renderedFrom === ModuleType.Studies ? studiesState : reportsState;

	const { selectedStudy, selectedStudyPageTab, selectedReportsPageTab, folderDropdownData, allStudyTypes } = moduleState;

	const hasAccess = selectedStudy?.access_type === userAccessType.edit || selectedStudy?.access_type === userAccessType.view;
	const studyId = selectedStudy?.study_id;

	const handleTypeChange = (values: any[]) => {
		// Standard "Select All" logic:
		if (values.includes("All") && !selectedTypes.includes("All")) {
			setSelectedTypes(["All"]);
		} else {
			const filtered = values.filter((v) => v !== "All");
			setSelectedTypes(filtered.length > 0 ? filtered : ["All"]);
		}
	};

	const studyTypeDropdownDataConfig = useMemo(() => {
		return allStudyTypes?.map((type) => ({
			label: t(type?.ask_kural_display_name) || t(type?.display_name) || t(type?.label),
			value: t(type?.label),
			disabled: type?.metadata?.disabled ?? false,
			is_hidden: type?.metadata?.is_hidden ?? false,
			additional_text: type?.metadata?.additional_text,
		}));
	}, [allStudyTypes]);

	const availableStudyTypeDropdownItems = useMemo(() => {
		if (!studyTypeDropdownDataConfig) return [];
		return studyTypeDropdownDataConfig.filter((item) => !item.is_hidden);
	}, [studyTypeDropdownDataConfig]);

	const showCurrentFolder = useMemo(() => {
		if (isStudies) {
			return currentFolder !== null && selectedStudyPageTab === StudyPageTabType.All;
		} else if (isReports) {
			return currentFolder !== null && selectedReportsPageTab === ReportsHomePageTabType.All;
		}
		return false;
	}, [currentFolder, selectedStudyPageTab, selectedReportsPageTab, isStudies, isReports]);

	// Tag management logic
	useEffect(() => {
		if (type === "button" && tags?.length > 0 && !tags.includes("All")) {
			tags = ["All", ...tags];
		}

		let updatedTags = tags;
		if (fromModal) {
			const remainingTags = updatedTags.filter((tag) => tag !== "All" && tag !== activeTag);
			if (activeTag === "All") {
				updatedTags = [activeTag, ...remainingTags];
			} else {
				updatedTags = ["All", activeTag, ...remainingTags];
			}

			if (updatedTags.includes(activeTag)) {
				updatedTags = updatedTags.filter((tag) => tag !== activeTag);
				if (updatedTags?.length >= 5) {
					updatedTags.splice(5, 0, activeTag);
				} else {
					updatedTags.push(activeTag);
				}
			}
		}

		if (JSON.stringify(updatedTags) !== JSON.stringify(tagList)) {
			setTagList(updatedTags);
		}
	}, [tags, fromModal, activeTag, type, tagList]);

	const handleTagClick = useCallback(
		(tag: string, isModalClicked: boolean) => {
			setFromModal(isModalClicked);
			setActiveTag(tag);
			onTagSelect?.(tag);
		},
		[onTagSelect],
	);

	const showAddCollabModal = () => {
		setCollabModalKey((prevKey) => prevKey + 1);
		setIsAddCollabModalOpen(true);
	};

	const handleOk = () => {
		setIsAddCollabModalOpen(false);
	};

	const handleCancel = () => {
		setIsAddCollabModalOpen(false);
	};

	const transformToPayload = useCallback(
		(data: any[]): ILevel1[] => {
			if (isStudies) {
				const studiesPayload = data?.map((study) => {
					const parentFolder = folderDropdownData?.find((folder: any) => folder.studies?.some((s: any) => s.study_id === study.study_id));

					return {
						level1Id: study.study_id,
						level1Name: study.study_name,
						lstLevel2s: study.custom_tags?.map((tag: string) => ({ level2Id: tag, level2Name: tag })),
						collaborators: study.collaborators,
						insightsAvailable: study.insights_available,
						access_type: study.user_access_type,
						insights_id: study.active_insights_id,
						created_by: study.version_history.created_by,
						study_type: study.study_type,
						quant_tabs: study.quant_tabs,
						last_updated_at: study.version_history.modified_dt,
						is_date_mapped: study.is_date_mapped ?? false,
						folder: parentFolder
							? {
								folder_id: parentFolder.folder_id,
								folder_name: parentFolder.folder_name,
							}
							: null,
						isFolder: false,
					};
				});
				// Add folders as separate searchable items
				const foldersPayload =
					folderDropdownData
						?.filter((f) => f?.folder_id && f?.folder_name)
						.map((folder: any) => ({
							level1Id: folder.folder_id,
							level1Name: folder.folder_name,
							lstLevel2s:
								folder.folder_tags?.map((tag: string) => ({
									level2Id: tag,
									level2Name: tag,
								})) || [],
							collaborators: [],
							insightsAvailable: false,
							access_type: null,
							insights_id: null,
							created_by: null,
							study_type: "folder",
							quant_tabs: null,
							last_updated_at: folder.version_history?.modified_dt,
							is_date_mapped: false,
							folder: null,
							isFolder: true,
							study_count: folder.study_count || folder.studies?.length || 0,
							folderData: folder,
						})) ?? [];
				return [...foldersPayload, ...studiesPayload];
			} else {
				return data?.map((report) => ({
					level1Id: report.report_iteration_id || report.report_gen_id,
					level1Name: report.report_name || report.name,
					lstLevel2s: report.custom_tags?.map((tag: string) => ({ level2Id: tag, level2Name: tag })) || [],
					collaborators: report?.permissions?.collaborators || [],
					insightsAvailable: report.insights_available || false,
					access_type: report.user_access_type || userAccessType.view,
					insights_id: report.active_insights_id || null,
					created_by: report.version_history?.created_by || report.created_by,
					study_type: report.file_type || "report",
					quant_tabs: report.quant_tabs || [],
					last_updated_at: report.version_history?.modified_dt || report.updated_at,
					is_date_mapped: report.is_date_mapped ?? false,
					folder: null,
				}));
			}
		},
		[isStudies, folderDropdownData],
	);

	// Extract unique collaborators
	const extractUniqueCollaborators = useCallback(
		(data: any[]) => {
			const collaboratorMap = new Map();

			data?.forEach((item) => {
				const allCollabs = isStudies ? item?.collaborators : isReports ? item?.permissions?.collaborators : [];
				allCollabs?.forEach((collaborator: any) => {
					if (!collaboratorMap.has(collaborator.collaborator_id)) {
						collaboratorMap.set(collaborator.collaborator_id, {
							collaboratorId: collaborator.collaborator_id,
							collaboratorName: collaborator.collaborator_name,
							collaboratorEmail: collaborator.collaborator_email,
						});
					}
				});
			});

			return Array.from(collaboratorMap.values());
		},
		[isStudies, isReports],
	);

	const onCollaboratorSearch = useCallback(
		(collaborators: ITag[]) => {
			if (isStudies) {
				dispatch(setSelectedCollaborators(collaborators));
			} else if (isReports) {
				dispatch(setReportsSelectedCollaborators(collaborators));
			}
		},
		[dispatch, isStudies, isReports],
	);

	const onClickSearch = useCallback(
		(value: string) => {
			// Check if the search value matches a folder name
			const matchedFolder = folderDropdownData?.find((folder: any) => folder.folder_name?.toLowerCase() === value?.toLowerCase());

			if (matchedFolder && onFolderSearchClick) {
				onFolderSearchClick(matchedFolder); // Navigate into folder instead
				return;
			}

			if (isStudies) {
				dispatch(setSearchText(value));
			} else if (isReports) {
				dispatch(setReportsSearchText(value));
			}
		},
		[dispatch, isStudies, isReports, folderDropdownData, onFolderSearchClick],
	);

	const onChange = useCallback((value: string) => { }, []);

	const handlePageTabChange = useCallback(
		(value: string) => {
			if (currentFolder && onBackClick) {
				onBackClick();
			}
			setLoading(true);
			if (isStudies) {
				dispatch(setSelectedStudyPageTab(value as StudyPageTabType));
				if (
					value === StudyPageTabType.All ||
					value === StudyPageTabTypeRevamp.Collections ||
					value === StudyPageTabTypeRevamp.Starred ||
					value === StudyPageTabTypeRevamp.Studies
				) {
					dispatch(setSelectedStudyPageView(StudyPageViewType.Table));
				} else {
					dispatch(setSelectedStudyPageView(StudyPageViewType.Grid));
				}
			} else if (isReports) {
				dispatch(setSelectedReportsPageTab(value as ReportsHomePageTabType));
				if (value === ReportsHomePageTabType.All) {
					dispatch(setSelectedReportsPageView(ReportsPageViewType.Table));
				} else {
					dispatch(setSelectedReportsPageView(ReportsPageViewType.Grid));
				}
			}
		},
		[dispatch, isStudies, isReports, currentFolder, onBackClick],
	);

	const pageTabOptions = useMemo(() => {
		const tooltipTitle = currentFolder ? t("Exit the collection to switch tabs") : null;

		const mapOption = (option: string) => ({
			label: (
				<Tooltip getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body} title={tooltipTitle}>
					<div style={{ pointerEvents: "all" }}>{t(option)}</div>
				</Tooltip>
			),
			value: option,
		});

		if (isStudies) {
			return Object.keys(StudyPageTabTypeRevamp)
				?.filter((option) => option !== StudyPageTabTypeRevamp.Collections || isFolderEnabled)
				?.map(mapOption);
		} else if (isReports) {
			return Object.keys(ReportsHomePageTabType)?.map(mapOption);
		}
	}, [isStudies, isReports, isFolderEnabled, currentFolder, t]);

	const handleCreateReportNavigation = useCallback(() => {
		if (isReportsModuleEnabled) {
			dispatch(resetSelectedReportState());
			navigate("/add-report");
		} else {
			console.warn("Reports module is not enabled.");
		}
	}, [isReportsModuleEnabled, navigate]);

	const getDefaultButtons = useMemo(() => {
		const buttons: React.ReactNode[] = [];

		if (isReports && isReportsModuleEnabled) {
			buttons.push(
				<Button type="primary" key="addReport" onClick={handleCreateReportNavigation}>
					{t("Create Report")}
				</Button>,
			);
		}

		return buttons;
	}, [isReports, isReportsModuleEnabled, handleCreateReportNavigation, t]);

	const buttonsToRender = customButtons || getDefaultButtons;

	const handleBackButtonClick = () => {
		onBackClick?.();
	};

	return (
		<>
			<div className={`home-page-subHeader-nav`}>
				<div className={`${isStudies ? "flex items-center justify-center gap-6" : "flex items-center justify-center gap-12"}`}>
					<div className="flex gap-4 items-center justify-center">
						<Segmented
							className={`home-page-study-recent-starred-segmented ${isStudies ? "studies-segmented-pills" : ""} ${currentFolder ? "segmented-disabled" : ""}`}
							onChange={handlePageTabChange}
							value={renderedFrom === ModuleType.Studies ? selectedStudyPageTab : selectedReportsPageTab}
							defaultValue={renderedFrom === ModuleType.Studies ? selectedStudyPageTab : selectedReportsPageTab}
							options={pageTabOptions}
							disabled={!!currentFolder}
							style={{ pointerEvents: currentFolder ? "none" : "auto" }}
						/>
						{isStudies && <MultiSelectStudyDropdownWrapper
							availableStudyTypeDropdownItems={availableStudyTypeDropdownItems}
							selectedType={selectedTypes}
							onTypeChange={handleTypeChange}
							isCompDisabled={false}
							isLoading={false}
							isStandardQueryMode={true}
							chatStarted={false}
							isProcessComplete={true}
							studyTypeFilterDisableItems={[]}
							isHomePage={true}
						/>}
					</div>
					<Searchbar
						renderedFrom={renderedFrom}
						payload={transformToPayload(allData || [])}
						collaborators={extractUniqueCollaborators(allData || [])}
						onCollaboratorSearch={onCollaboratorSearch}
						onChange={onChange}
						onClickSearch={onClickSearch}
						onFolderClick={onFolderSearchClick}
					/>
				</div>
				<div className="flex items-center gap-4 ml-4">
					{/* Show topRowButtons if provided, otherwise show default */}
					{topRowButtons && topRowButtons.length > 0
						? topRowButtons.map((button, index) => <span key={index}>{button}</span>)
						: buttonsToRender.map((button, index) => <span key={index}>{button}</span>)}
				</div>
			</div>
			{currentFolder && !(renderedFrom === ModuleType.Studies) && (
				<div className="home-page-subHeader-bread">
					<div className="flex items-center justify-center gap-12">
						<div className="flex gap-2 items-center justify-center">
							<button
								className={`flex items-center ${showCurrentFolder ? "cursor-pointer" : "cursor-default"}`}
								onClick={showCurrentFolder ? handleBackButtonClick : undefined}
								disabled={!showCurrentFolder}>
								<i className={`material-symbols-outlined ${showCurrentFolder ? "text-[--secondary-color-2]" : "text-[#D9D9D9]"} text-2xl`}>arrow_back</i>
							</button>
							{!isStudies && <span className="font-normal text-base-14 secondary-color">{pageTitle} / </span>}
							<span className="font-normal text-base-14 text-[--neutral-grey] secondary-color">{t(selectedReportsPageTab)}</span>
							{showCurrentFolder && (
								<>
									<i className="material-symbols-outlined text-2xl">chevron_right</i>
									<Folder />
									<span className="font-normal text-base-14 text-[--neutral-grey]">{currentFolder.folder_name}</span>
								</>
							)}
						</div>
					</div>
					<div className="flex items-center gap-4 pl-4 ml-auto">
						{secondButton?.map((button, index) => (
							<span key={index}>{button}</span>
						))}
					</div>
				</div>
			)}
		</>
	);
};

export default React.memo(HomePageSubHeader);
